import { useLayoutEffect, useRef, type ReactNode, type TableHTMLAttributes } from "react";
import { cn } from "@/lib/utils";

/**
 * A table that never scrolls sideways: when its columns cannot fit even with their
 * text wrapped, each row stacks as a block of "column: value" lines, the column
 * names taken from its header (styles/table.css). The caller writes the rows
 * (thead, tbody, tfoot, th scope="row") as plain markup; this draws them. With a
 * label the table is a named region. Extra props go on the <table>.
 */
export function Table({
  label,
  className,
  tableClassName,
  children,
  ...table
}: {
  label?: string;
  className?: string;
  tableClassName?: string;
  children: ReactNode;
} & TableHTMLAttributes<HTMLTableElement>) {
  const box = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const el = box.current!;
    let need = 0;
    // Sideways scrolling is only a net; while it does scroll, a keyboard must be able to reach it (WCAG 2.1.1).
    const fit = () => {
      el.toggleAttribute("data-stacked", el.clientWidth < need);
      if (el.scrollWidth > el.clientWidth || el.scrollHeight > el.clientHeight) el.tabIndex = 0;
      else el.removeAttribute("tabindex");
    };
    // Its own header and rows only: a row may open a table of its own.
    const measure = () => {
      const t = el.querySelector(":scope > table") as HTMLTableElement | null;
      if (!t) return;
      const heads = [...t.querySelectorAll(":scope > thead > tr > th")].map((th) => {
        const name = th.cloneNode(true) as Element; // without its decoration, such as a sort mark
        name.querySelectorAll('[aria-hidden="true"]').forEach((e) => e.remove());
        return name.textContent?.trim() ?? "";
      });
      // A cell spanning columns belongs to none of them; a cell after one takes the column it sits in.
      for (const row of t.querySelectorAll(":scope > tbody > tr, :scope > tfoot > tr")) {
        let col = 0;
        for (const cell of row.children as HTMLCollectionOf<HTMLTableCellElement>) {
          cell.setAttribute("data-label", cell.colSpan > 1 ? "" : (heads[col] ?? ""));
          col += cell.colSpan;
        }
      }
      el.removeAttribute("data-stacked");
      t.style.width = "min-content";
      need = Math.ceil(t.getBoundingClientRect().width);
      t.style.width = "";
      fit();
    };
    measure();
    // Rows change without this component rendering (a row opening itself), so watch the content.
    const content = new MutationObserver(measure);
    content.observe(el, { childList: true, subtree: true, characterData: true });
    const width = new ResizeObserver(fit);
    width.observe(el);
    // A web font that arrives after the first measure widens the text without a mutation.
    let live = true;
    void document.fonts?.ready.then(() => { if (live) measure(); });
    document.fonts?.addEventListener("loadingdone", measure);
    return () => {
      live = false;
      content.disconnect();
      width.disconnect();
      document.fonts?.removeEventListener("loadingdone", measure);
    };
  }, []);
  return (
    <div ref={box} data-slot="table" role={label ? "region" : undefined} aria-label={label} className={cn("relative overflow-x-auto", className)}>
      <table className={cn("w-full", tableClassName)} {...table}>
        {children}
      </table>
    </div>
  );
}
